import { Injectable, inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotificationType =
  | 'FRIEND_REQUEST'
  | 'FRIEND_REQUEST_ACCEPTED'
  | 'FRIEND_REQUEST_DECLINED'
  | 'FOLLOW'
  | 'POST_LIKE'
  | 'POST_COMMENT'
  | 'POST_MENTION'
  | 'POST_TAG'
  | 'POST_COLLABORATION'
  | 'POST_COLLABORATION_ACCEPTED'
  | 'POST_COLLABORATION_DECLINED'
  | 'BLOG_LIKE'
  | 'BLOG_COMMENT'
  | 'BLOG_MENTION'
  | 'BLOG_TAG'
  | 'BLOG_COLLABORATION'
  | 'BLOG_COLLABORATION_ACCEPTED'
  | 'BLOG_COLLABORATION_DECLINED'
  | 'BLOG_PUBLISHED'
  | 'EVENT_LIKE'
  | 'EVENT_COMMENT'
  | 'EVENT_MENTION'
  | 'EVENT_TAG'
  | 'EVENT_COLLABORATION'
  | 'EVENT_COLLABORATION_ACCEPTED'
  | 'EVENT_COLLABORATION_DECLINED'
  | 'MENTION'
  | 'SYSTEM';

export interface ISenderSnippet {
  _id:          string;
  displayName:  string;
  userName:     string;
  profileImage: { urlSmall?: string; urlMedium?: string } | null;
}

// ── Flat notification (used for real-time socket push + legacy list) ──────────
export interface INotification {
  _id:      string;
  type:     NotificationType;
  title:    string;
  message:  string;
  isRead:   boolean;
  data?:    Record<string, unknown>;
  sender:   ISenderSnippet;
  createdAt: string;
}

// ── Grouped notification (Instagram-style, returned by /grouped endpoint) ─────
export interface IGroupedNotification {
  groupKey:        string;
  type:            NotificationType;
  latestSender:    ISenderSnippet;
  senders:         ISenderSnippet[];   // up to 3 for avatar stack
  actorCount:      number;
  message:         string;
  data:            Record<string, unknown>;
  isRead:          boolean;
  latestId:        string;
  notificationIds: string[];
  createdAt:       string;
}

// ── API response shapes ───────────────────────────────────────────────────────

interface INotificationListResponse {
  success: boolean;
  data: {
    items: INotification[];
    meta: { page: number; limit: number; total: number; unreadCount: number };
  };
}

interface IGroupedNotificationResponse {
  success: boolean;
  data: {
    groups: IGroupedNotification[];
    meta:   { page: number; limit: number; total: number; unreadCount: number };
  };
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {

  private http       = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private base       = `${environment.apiUrl}/notifications`;

  private socket: Socket | null = null;

  // ── State streams ───────────────────────────────────────────────────────────
  readonly notifications$ = new BehaviorSubject<INotification[]>([]);
  readonly groups$        = new BehaviorSubject<IGroupedNotification[]>([]);
  readonly toasts$        = new BehaviorSubject<INotification[]>([]);
  readonly unreadCount$   = new BehaviorSubject<number>(0);

  // ── Socket ──────────────────────────────────────────────────────────────────

  connect(token: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.socket?.connected) this.disconnect();

    this.socket = io(environment.socketUrl, {
      auth:       { token },
      transports: ['websocket'],
    });

    this.socket.off('notification:new');
    this.socket.on('notification:new', (notification: INotification) => {
      // Prepend to flat list
      this.notifications$.next([notification, ...this.notifications$.value]);
      this.unreadCount$.next(this.unreadCount$.value + 1);
      this.pushToast(notification);

      // Relationship types (FRIEND_REQUEST / FOLLOW) are upserted server-side
      // on a (recipient, sender, type) key — a resend/refollow reuses the
      // same notification id. Match on that so the panel updates the
      // existing card in place instead of adding a duplicate one on top.
      const isRelationship =
        notification.type === 'FRIEND_REQUEST' || notification.type === 'FOLLOW';

      const existingIndex = isRelationship
        ? this.groups$.value.findIndex(
            g => g.type === notification.type && g.latestSender._id === notification.sender._id,
          )
        : -1;

      if (existingIndex >= 0) {
        const updated = [...this.groups$.value];
        const prior = updated[existingIndex];
        updated[existingIndex] = {
          ...prior,
          message:         notification.message,
          data:            notification.data ?? {},
          isRead:          false,
          latestId:        notification._id,
          notificationIds: [notification._id],
          createdAt:       notification.createdAt,
        };
        // Move it back to the top so it reads as "just happened"
        const [moved] = updated.splice(existingIndex, 1);
        this.groups$.next([moved, ...updated]);
        return;
      }

      // New group at top
      const newGroup: IGroupedNotification = {
        groupKey:        `${notification.type}:${notification._id}`,
        type:            notification.type,
        latestSender:    notification.sender,
        senders:         [notification.sender],
        actorCount:      1,
        message:         notification.message,
        data:            notification.data ?? {},
        isRead:          false,
        latestId:        notification._id,
        notificationIds: [notification._id],
        createdAt:       notification.createdAt,
      };
      this.groups$.next([newGroup, ...this.groups$.value]);
    });

    // Server removed a relationship notification (unsent friend request,
    // unfollow, accept/decline, block) — drop it from the live list.
    this.socket.off('notification:removed');
    this.socket.on('notification:removed', (payload: { notificationId: string; type: NotificationType; sender: string }) => {
      const wasUnread = this.groups$.value.find(
        g => g.type === payload.type && g.latestSender._id === payload.sender,
      )?.isRead === false;

      this.groups$.next(
        this.groups$.value.filter(g => !(g.type === payload.type && g.latestSender._id === payload.sender)),
      );
      this.notifications$.next(
        this.notifications$.value.filter(n => n._id !== payload.notificationId),
      );
      if (wasUnread) {
        this.unreadCount$.next(Math.max(0, this.unreadCount$.value - 1));
      }
    });

    // Broader cleanup (e.g. block) — safest to just re-fetch unread count;
    // the panel re-fetches its lists on next open via resetAndLoad().
    this.socket.off('notification:refresh');
    this.socket.on('notification:refresh', () => this.fetchUnreadCount());

    this.socket.off('connect_error');
    this.socket.on('connect_error', (err) => {
      if (err.message === 'Unauthorized') this.disconnect();
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  // ── REST ────────────────────────────────────────────────────────────────────

  getNotifications(page = 1, limit = 20): Observable<INotificationListResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<INotificationListResponse>(
      `${this.base}/${page}/list?limit=${limit}`
    );
  }

  getGrouped(page = 1, limit = 20): Observable<IGroupedNotificationResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IGroupedNotificationResponse>(
      `${this.base}/grouped?page=${page}&limit=${limit}`
    );
  }

  /** Fetches just the unread count from the grouped endpoint and updates the badge. */
  fetchUnreadCount(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.http.get<IGroupedNotificationResponse>(`${this.base}/grouped?page=1&limit=1`)
      .subscribe({
        next: res => this.unreadCount$.next(res.data.meta.unreadCount),
        error: () => { /* silent — badge stays 0 if request fails */ },
      });
  }

  markRead(notificationId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.base}/${notificationId}/read`, {});
  }

  markGroupRead(notificationIds: string[]): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.base}/read-group`, { notificationIds });
  }

  markAllRead(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.base}/read-all`, {});
  }

  // ── Local state helpers ─────────────────────────────────────────────────────

  markLocalRead(id: string): void {
    this.notifications$.next(
      this.notifications$.value.map(n => n._id === id ? { ...n, isRead: true } : n)
    );
    this.unreadCount$.next(Math.max(0, this.unreadCount$.value - 1));
  }

  markGroupLocalRead(notificationIds: string[]): void {
    const idSet = new Set(notificationIds);
    this.groups$.next(
      this.groups$.value.map(g =>
        g.notificationIds.some(id => idSet.has(id)) ? { ...g, isRead: true } : g
      )
    );
    // Recalculate unread count
    const unread = this.groups$.value.filter(g => !g.isRead).length;
    this.unreadCount$.next(unread);
  }

  markAllLocalRead(): void {
    this.notifications$.next(this.notifications$.value.map(n => ({ ...n, isRead: true })));
    this.groups$.next(this.groups$.value.map(g => ({ ...g, isRead: true })));
    this.unreadCount$.next(0);
  }

  // ── Toast helpers ───────────────────────────────────────────────────────────

  private pushToast(notification: INotification): void {
    this.toasts$.next([...this.toasts$.value, notification]);

    // Auto-dismiss after 5 seconds (friend-requests and collab invites stay longer)
    const isActionable =
      notification.type === 'FRIEND_REQUEST'       ||
      notification.type === 'POST_COLLABORATION'   ||
      notification.type === 'BLOG_COLLABORATION'   ||
      notification.type === 'EVENT_COLLABORATION';

    const delay = isActionable ? 10_000 : 5_000;

    setTimeout(() => this.dismissToast(notification._id), delay);
  }

  dismissToast(id: string): void {
    this.toasts$.next(this.toasts$.value.filter(n => n._id !== id));
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
